import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type { UpsertMethodologyRequest } from '../../schema/data-contracts';
import type { MethodologyListParams, MutationProps } from '../types';

const getMethodologiesQueryKey = 'methodologies';

export const useMethodologyQueryActions = () => {
  const { auth } = useAuth();
  const {
    methodologyController: { v1MethodologiesList, v1MethodologiesUpdate },
    methodologyWithCustomCultureController: {
      v1MethodologiesList: v1MethodologiesListWithCustomCulture,
    },
  } = useApi();

  const queryClient = useQueryClient();

  const useGetMethodologies = (
    cultureName: string | undefined,
    params: MethodologyListParams,
    predicate?: boolean,
  ) =>
    useQuery({
      enabled: Boolean(auth) && predicate !== false,
      queryKey: [getMethodologiesQueryKey, params, cultureName],
      queryFn: async () => {
        const { data } = cultureName
          ? await v1MethodologiesListWithCustomCulture(params, {
              headers: { 'Accept-Language': cultureName },
            })
          : await v1MethodologiesList(params);
        return data;
      },
    });

  const useUpdateMethodologies = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['update-methodologies'],
      mutationFn: async (upsertMethodology: UpsertMethodologyRequest[]) => {
        const { data } = await v1MethodologiesUpdate(upsertMethodology);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [getMethodologiesQueryKey] });
      },
    });

  return { useGetMethodologies, useUpdateMethodologies };
};
